import { useRef, useState, type ChangeEvent } from 'react'
import type {
  CharacterSheet,
  CharacterSummary,
  ImportProblem,
} from '@rollcall/contracts'
import { EXPORT_SIZE_LIMIT_BYTES } from '@rollcall/contracts'
import type { Api } from '../api.js'
import { ImportRefused } from '../api.js'
import { downloadFile, markdownFileName } from '../download.js'
import { sheetMarkdown } from '../sheet-markdown.js'
import { useT } from './i18n-context.js'

/**
 * Файл персонажа: выгрузка с листа и загрузка из списка (D54).
 *
 * Форматов два, и они разной природы. JSON содержит журнал решений
 * и возвращается обратно; Markdown — лист как есть, для человека,
 * и обратно не разбирается. Собирает JSON сервер (журнал у него),
 * Markdown — клиент (перевод и подписи у него).
 */

/**
 * Меню «Экспортировать» (D54, D64): куда отдать персонажа.
 *
 * Три пути, и они разной природы: файл персонажа — на другое
 * устройство, лист текстом — в заметки, печатный лист — на бумагу.
 * Раньше первые два стояли кнопками внизу листа, и третья сделала бы
 * из низа листа ряд кнопок, которые нажимают раз в месяц. Меню
 * держит их в одном месте и не занимает лист, пока не нужно.
 *
 * Отдельной кнопки «Сохранить как PDF» нет: это делает окно печати
 * браузера, и печатный экран говорит, где это там.
 */
export function ExportMenu({
  api,
  sheet,
  onPrint,
}: {
  readonly api: Api
  readonly sheet: CharacterSheet
  /** Открыть печатный лист; не задано — пункта нет. */
  readonly onPrint?: (() => void) | undefined
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const save = (markdown: boolean) => {
    setBusy(true)
    setFailure(null)
    void api
      .exportCharacter(sheet.character.id)
      .then((result) => {
        if (markdown) {
          downloadFile(
            document,
            markdownFileName(result.fileName),
            sheetMarkdown(t, sheet),
            'text/markdown',
          )
          return
        }
        downloadFile(
          document,
          result.fileName,
          `${JSON.stringify(result.file, null, 2)}\n`,
          'application/json',
        )
      })
      .catch(() => {
        setFailure(t('errors.network'))
      })
      .finally(() => {
        setBusy(false)
      })
  }

  const option = (label: string, hint: string, testId: string, action: () => void) => (
    <li className="app-export-option">
      <button
        type="button"
        className="app-button app-button-quiet"
        disabled={busy}
        data-testid={testId}
        onClick={action}
      >
        {label}
      </button>
      <span className="app-field-hint">{hint}</span>
    </li>
  )

  return (
    <div className="app-screen">
      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          aria-expanded={open}
          aria-controls="export-options"
          data-testid="export-menu"
          onClick={() => {
            setOpen(!open)
          }}
        >
          {t('transfer.menu')}
        </button>
      </div>
      {open && (
        <div id="export-options">
          <ul className="app-export-options">
            {option(
              t('transfer.toDevice'),
              t('transfer.toDeviceHint'),
              'export-json',
              () => {
                save(false)
              },
            )}
            {option(
              t('transfer.toText'),
              t('transfer.toTextHint'),
              'export-markdown',
              () => {
                save(true)
              },
            )}
            {onPrint !== undefined &&
              option(
                t('transfer.toPrint'),
                t('transfer.toPrintHint'),
                'export-print',
                onPrint,
              )}
          </ul>
          <p className="app-field-hint">{t('transfer.exportHint')}</p>
        </div>
      )}
      {failure !== null && (
        <p className="app-problems" role="alert">
          {failure}
        </p>
      )}
    </div>
  )
}

/**
 * Загрузка файла.
 *
 * Записи контента из файла сами не заводятся: сервер возвращает
 * список того, чего не хватает, и заводит его только по отдельному
 * согласию (D54). Поэтому здесь два состояния, а не одно: отказ
 * со списком и повтор с согласием.
 */
export function ImportPanel({
  api,
  onImported,
}: {
  readonly api: Api
  readonly onImported: (character: CharacterSummary) => void
}) {
  const t = useT()
  const input = useRef<HTMLInputElement>(null)
  const [text, setText] = useState<string | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [problems, setProblems] = useState<readonly ImportProblem[]>([])
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const send = (content: string, adoptContent: boolean) => {
    setBusy(true)
    setFailure(null)
    setProblems([])
    void api
      .importCharacter(content, adoptContent)
      .then((result) => {
        setText(null)
        setFileName(null)
        if (input.current !== null) input.current.value = ''
        onImported(result.character)
      })
      .catch((cause: unknown) => {
        if (cause instanceof ImportRefused) setProblems(cause.problems)
        else setFailure(t('errors.network'))
      })
      .finally(() => {
        setBusy(false)
      })
  }

  const choose = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file === undefined) return
    setProblems([])
    setFailure(null)
    // Предел проверяется и здесь, и на сервере. Здесь — чтобы
    // не гонять мегабайты по сети ради заведомого отказа; на сервере —
    // потому что клиент проверкам не судья.
    if (file.size > EXPORT_SIZE_LIMIT_BYTES) {
      setFailure(t('transfer.tooLarge'))
      return
    }
    void file.text().then((content) => {
      setText(content)
      setFileName(file.name)
      send(content, false)
    })
  }

  // Завести записи можно, только если все они есть в файле: половина
  // записей — это персонаж, который не считается.
  const adoptable =
    problems.length > 0 &&
    problems.every((problem) => problem.inFile && problem.code === 'missing-content')

  return (
    <div className="app-import">
      <label className="app-field">
        <span className="app-field-label">{t('transfer.import')}</span>
        <input
          ref={input}
          type="file"
          accept="application/json,.json"
          data-testid="import-file"
          onChange={choose}
        />
        <span className="app-field-hint">{t('transfer.importHint')}</span>
      </label>

      {failure !== null && (
        <p className="app-problems" role="alert">
          {failure}
        </p>
      )}

      {problems.length > 0 && (
        <div className="app-problems" role="alert" data-testid="import-problems">
          <p>{t('transfer.refused', { file: fileName ?? '' })}</p>
          <ul>
            {problems.map((problem) => (
              <li key={`${problem.code}-${problem.id ?? problem.message}`}>
                {problem.name === null
                  ? problem.message
                  : `«${problem.name}» — ${problem.message}`}
              </li>
            ))}
          </ul>
          {adoptable && text !== null && (
            <button
              type="button"
              className="app-button"
              disabled={busy}
              data-testid="import-adopt"
              onClick={() => {
                send(text, true)
              }}
            >
              {t('transfer.adopt', { count: problems.length })}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
